import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import io from 'socket.io-client';
import { QRCodeSVG } from 'qrcode.react';
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import NotFound from './NotFound';
import OutOfStock from './OutOfStock';

const FeedbackForm = ({ orderId, onClose, onFeedbackSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState('');
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [orderDetails, setOrderDetails] = useState(null);
  const [hasExistingFeedback, setHasExistingFeedback] = useState(false);

  useEffect(() => {
    // Vérifier d'abord si un feedback existe déjà
    fetch(`${process.env.REACT_APP_API_URL}/api/feedbacks/check/${orderId}`)
      .then(res => res.json())
      .then(data => {
        if (data.exists) {
          setHasExistingFeedback(true);
          onFeedbackSubmitted(data.feedback);
          onClose();
          return;
        }
        // Si pas de feedback existant, récupérer les détails de la commande
        return fetch(`${process.env.REACT_APP_API_URL}/api/invoice/${orderId}`)
          .then(res => res.json())
          .then(data => {
            setOrderDetails(data);
          });
      })
      .catch(error => {
        console.error('Error:', error);
        setError('Failed to check feedback status');
      });
  }, [orderId, onFeedbackSubmitted, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (hasExistingFeedback) {
      onClose();
      return;
    }
    
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }
    if (!orderDetails?.product?.id) {
      setError('Unable to submit feedback: missing product information');
      return;
    }
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/feedbacks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderId,
          productId: orderDetails.product.id,
          rating,
          message
        })
      });

      const data = await response.json();

      if (response.ok) {
        onFeedbackSubmitted(data);
        onClose();
      } else {
        if (data.error === 'Feedback already exists for this order') {
          setHasExistingFeedback(true);
          onFeedbackSubmitted(data.feedback);
          onClose();
        } else {
          setError(data.error || 'Failed to submit feedback');
        }
      }
    } catch (error) {
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (hasExistingFeedback) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="glass-card p-6 max-w-md w-full">
          <h3 className="text-xl font-semibold mb-4">Leave Feedback</h3>
          <div className="text-red-500 text-sm mb-4">A feedback has already been submitted for this order</div>
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="neon-button"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="glass-card p-6 max-w-md w-full">
        <h3 className="text-xl font-semibold mb-4">Leave Feedback</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block mb-2">Rating</label>
            <div className="flex space-x-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="focus:outline-none transition-transform hover:scale-110"
                >
                  {star <= (hoveredRating || rating) ? (
                    <StarIconSolid className="h-8 w-8 review-star filled" />
                  ) : (
                    <StarIconOutline className="h-8 w-8 review-star" />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block mb-2">Message (optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="form-input w-full"
              rows="4"
              placeholder="Tell us about your experience..."
            />
          </div>

          {error && (
            <div className="text-red-500 text-sm">{error}</div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="neon-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="neon-button"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PaymentPage = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [orderDetails, setOrderDetails] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [socket, setSocket] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [deliveredItems, setDeliveredItems] = useState([]);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [existingFeedback, setExistingFeedback] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    // Initialize socket connection
    const socket = io(process.env.REACT_APP_API_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    socket.on('connect', () => {
      console.log('WebSocket connected successfully');
    });

    socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
    });

    socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
    });

    socket.on('reconnect', (attemptNumber) => {
      console.log('WebSocket reconnected after', attemptNumber, 'attempts');
    });

    socket.on('reconnect_error', (error) => {
      console.error('WebSocket reconnection error:', error);
    });

    socket.on('reconnect_failed', () => {
      console.error('WebSocket reconnection failed after all attempts');
    });

    setSocket(socket);

    // Fetch order details and check for existing feedback
    const fetchOrderAndFeedback = async () => {
      try {
        // Fetch order details
        const orderResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/invoice/${invoiceId}`);
        if (!orderResponse.ok) {
          throw new Error('Invoice not found');
        }
        const orderData = await orderResponse.json();
        
        if (!orderData || !orderData.id) {
          throw new Error('Invalid invoice data');
        }

        // Fetch feedback status
        const feedbackResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/feedbacks/check/${invoiceId}`);
        const feedbackData = await feedbackResponse.json();
        
        if (feedbackData.exists) {
          setExistingFeedback(feedbackData.feedback);
          setFeedbackSubmitted(true);
          setShowFeedbackForm(false);
        }
        
        // Update order details and status
        setOrderDetails(orderData);
        
        if (orderData.deliveredItems) {
          setDeliveredItems(orderData.deliveredItems);
        }
        
        // Update payment status
        if (orderData.status === 'out_of_stock' || orderData.deliveryError === 'out_of_stock') {
          setPaymentStatus('out_of_stock');
        } else if (orderData.status === 'cancelled') {
          setPaymentStatus('cancelled');
        } else if (orderData.status === 'forced') {
          setPaymentStatus('forced');
        } else if (orderData.status === 'delivered') {
          setPaymentStatus('delivered');
        } else if (orderData.status === 'paid') {
          setPaymentStatus('paid');
        } else if (new Date(orderData.expiresAt) < new Date()) {
          setPaymentStatus('expired');
        } else if (orderData.status === 'pending') {
          setPaymentStatus('pending');
        } else {
          setPaymentStatus(orderData.status);
        }

        const expiryTime = new Date(orderData.expiresAt).getTime();
        setTimeLeft(Math.max(0, Math.floor((expiryTime - Date.now()) / 1000)));

      } catch (error) {
        console.error('Error fetching data:', error);
        setNotFound(true);
      }
    };

    if (invoiceId) {
      fetchOrderAndFeedback();
    }

    return () => {
      console.log('Disconnecting Socket.IO');
      socket.close();
    };
  }, [invoiceId]);

  const handleBackToStore = (e) => {
    e.preventDefault();
    // Ne pas afficher la confirmation si la commande est payée et livrée
    if (paymentStatus === 'delivered' || paymentStatus === 'paid') {
      navigate('/');
      return;
    }
    setShowConfirmation(true);
  };

  const handleConfirmReturn = async () => {
    // Si la commande est déjà livrée ou payée, rediriger directement
    if (paymentStatus === 'delivered' || paymentStatus === 'paid' || paymentStatus === 'forced') {
      navigate('/');
      return;
    }

    try {
      console.log(`\n=== Starting Invoice Cancellation Process ===`);
      console.log(`Invoice ID: ${invoiceId}`);
      console.log(`Current Status: ${paymentStatus}`);
      console.log(`Time of cancellation: ${new Date().toISOString()}`);

      // Annuler l'invoice et arrêter les vérifications Tatum
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/invoices/${invoiceId}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'cancelled',
          stopTatumCheck: true,
          cancelReason: 'user_cancelled',
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`\n=== Invoice Cancellation Failed ===`);
        console.error(`Status Code: ${response.status}`);
        console.error(`Error Message: ${errorText}`);
        throw new Error(errorText);
      }

      const data = await response.json();
      console.log(`\n=== Invoice Cancellation Successful ===`);
      console.log(`New Status: ${data.status}`);
      console.log(`Payment Check Status: ${data.paymentCheckStatus || 'unknown'}`);
      console.log(`Cancellation Time: ${data.cancelledAt || new Date().toISOString()}`);

      // Émettre un événement de déconnexion via le socket
      if (socket) {
        console.log(`\n=== Emitting Socket Events ===`);
        socket.emit('cancel_payment_check', { invoiceId });
        console.log(`Emitted cancel_payment_check event for invoice ${invoiceId}`);
      }

      // Déconnecter le socket
      if (socket) {
        console.log(`\n=== Cleaning Up Resources ===`);
        console.log(`Disconnecting WebSocket...`);
        socket.disconnect();
        console.log(`WebSocket disconnected successfully`);
      }

      console.log(`\n=== Redirecting to Store ===`);
      navigate('/');
    } catch (error) {
      console.error(`\n=== Critical Error During Cancellation ===`);
      console.error(`Error Type: ${error.name}`);
      console.error(`Error Message: ${error.message}`);
      console.error(`Stack Trace: ${error.stack}`);
      
      // Nettoyage en cas d'erreur
      if (socket) {
        console.log(`\n=== Emergency Cleanup ===`);
        console.log(`Forcing WebSocket disconnect...`);
        socket.disconnect();
        console.log(`WebSocket disconnected (emergency)`);
      }

      // Rediriger même en cas d'erreur
      console.log(`\n=== Emergency Redirect ===`);
      navigate('/');
    }
  };

  useEffect(() => {
    if (socket && invoiceId) {
      console.log('Setting up Socket.IO event listeners for invoice:', invoiceId);
      
      socket.emit('join_invoice', invoiceId);
      console.log('Joined invoice room:', invoiceId);

      // Out of stock event
      socket.on('out_of_stock', (data) => {
        console.log('Received out_of_stock event:', data);
        if (data.invoiceId === invoiceId) {
          setOrderDetails(prev => ({
            ...prev,
            status: 'out_of_stock',
            deliveryError: 'out_of_stock',
            message: data.message
          }));
          setPaymentStatus('out_of_stock');
        }
      });

      // Product delivered event
      socket.on('connect', () => {
        console.log('WebSocket connected successfully');
      });

      socket.on('connect_error', (error) => {
        console.error('WebSocket connection error:', error);
      });

      socket.on('product_delivered', (data) => {
        console.log('Received product_delivered event:', data);
        if (data.invoiceId === invoiceId) {
          console.log('Invoice ID matches current order');
          let items = [];
          
          if (data.botUrl) {
            items = [data.botUrl];
          } else if (data.items) {
            items = data.items;
          }

          console.log('Delivered items:', items);

          if (orderDetails?.status !== 'out_of_stock' && orderDetails?.deliveryError !== 'out_of_stock') {
            setOrderDetails(prev => {
              console.log('Updating order details:', {
                ...prev,
                status: data.status || (prev.status === 'forced' ? 'forced' : 'delivered'),
                botUrl: data.botUrl || prev.botUrl,
                deliveredItems: items
              });
              return {
                ...prev,
                status: data.status || (prev.status === 'forced' ? 'forced' : 'delivered'),
                botUrl: data.botUrl || prev.botUrl,
                deliveredItems: items
              };
            });
            setPaymentStatus(prev => data.status || (prev === 'forced' ? 'forced' : 'delivered'));
            setDeliveredItems(items);
          }
        }
      });

      // Payment received event
      socket.on('payment_received', (data) => {
        console.log('Received payment_received event:', data);
        if (data.invoiceId === invoiceId) {
          if (orderDetails?.status !== 'out_of_stock' && orderDetails?.deliveryError !== 'out_of_stock') {
            setOrderDetails(prev => ({
              ...prev,
              status: prev.status === 'forced' ? 'forced' : 'paid',
              botUrl: data.botUrl || prev.botUrl
            }));
            setPaymentStatus(prev => prev === 'forced' ? 'forced' : 'paid');
          }
        }
      });

      // Delivery failed event
      socket.on('delivery_failed', (data) => {
        console.log('Received delivery_failed event:', data);
        if (data.invoiceId === invoiceId) {
          console.log('Updating payment status to delivery_failed');
          setPaymentStatus('delivery_failed');
        }
      });

      // Order expired event
      socket.on('order_expired', (data) => {
        console.log('Received order_expired event:', data);
        if (data.invoiceId === invoiceId) {
          console.log('Updating payment status to expired');
          setPaymentStatus('expired');
        }
      });

      // Add order cancelled event listener
      socket.on('order_cancelled', (data) => {
        console.log('Received order_cancelled event:', data);
        if (data.invoiceId === invoiceId) {
          console.log('Updating payment status to cancelled');
          setPaymentStatus('cancelled');
        }
      });

      return () => {
        console.log('Cleaning up Socket.IO event listeners');
        socket.off('payment_received');
        socket.off('product_delivered');
        socket.off('out_of_stock');
        socket.off('delivery_failed');
        socket.off('order_expired');
        socket.off('order_cancelled');
      };
    }
  }, [socket, invoiceId, orderDetails?.status, orderDetails?.deliveryError]);

  useEffect(() => {
    if (timeLeft === null) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Ajout du refresh automatique toutes les 30 secondes pendant le paiement
  useEffect(() => {
    if (paymentStatus === 'pending') {
      const refreshInterval = setInterval(() => {
        window.location.reload();
      }, 30000); // 30 secondes

      return () => clearInterval(refreshInterval);
    }
  }, [paymentStatus]);

  const renderDeliveredSection = () => (
    <div className="space-y-4">
      <div className="bg-[#22c55e]/20 text-[#22c55e] p-4 rounded-lg">
        Your product has been delivered! Details below:
      </div>
      <div className="glass-card p-6">
        <h3 className="text-lg font-medium text-white mb-4">Delivered Items</h3>
        <div className="glass-card p-4">
          <div className="space-y-2">
            {deliveredItems.map((item, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-white/10 last:border-0">
                <div className="flex-1">
                  {item.startsWith('http') ? (
                    <a 
                      href={item} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-[#22c55e] hover:text-[#15803d] break-all"
                    >
                      {item}
                    </a>
                  ) : (
                    <code className="text-sm font-mono text-gray-300 break-all">{item}</code>
                  )}
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(item)}
                  className="ml-4 text-[#22c55e] hover:text-[#15803d] transition-colors text-sm"
                >
                  Copy
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Instructions si c'est un produit explicit_api */}
        {orderDetails.product.deliveryType === 'explicit_api' && (
          <div className="mt-4 p-4 border-t border-white/10">
            <div className="text-[#22c55e] font-medium mb-2">📝 Instructions:</div>
            <ol className="list-decimal list-inside space-y-2 text-gray-300">
              <li>Add our bot to your Discord server using the link above</li>
              <li>Members will start joining within 5-10 minutes</li>
            </ol>
          </div>
        )}
        
        {/* Section Feedback */}
        <div className="mt-6 border-t border-white/10 pt-4">
          {!feedbackSubmitted && !existingFeedback && paymentStatus === 'delivered' && (
            <>
              <p className="text-gray-400 mb-4">
                We value your opinion! Please take a moment to share your experience with us.
              </p>
              <button
                onClick={() => setShowFeedbackForm(true)}
                className="neon-button w-full"
              >
                Leave Feedback
              </button>
            </>
          )}
          {(feedbackSubmitted || existingFeedback) && (
            <div className="bg-green-500/10 text-green-500 p-4 rounded-lg">
              <div className="text-center">
                <p className="font-medium">Thank you for your feedback! 🌟</p>
                <p className="text-sm mt-1">Your opinion helps us improve our services.</p>
              </div>
              <div className="mt-3 text-left">
                <div className="flex items-center space-x-2">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      star <= (existingFeedback?.rating || 0) ? (
                        <StarIconSolid key={star} className="h-5 w-5 text-yellow-500" />
                      ) : (
                        <StarIconOutline key={star} className="h-5 w-5 text-gray-400" />
                      )
                    ))}
                  </div>
                </div>
                {existingFeedback?.message && (
                  <p className="mt-2 text-gray-300">{existingFeedback.message}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderPaymentInstructions = () => {
    if (paymentStatus !== 'pending') return null;

    if (orderDetails.paymentMethod === 'litecoin') {
      return (
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-white">Payment Details</h3>
            <div className="text-sm text-[#22c55e]">
              Expires in: {formatTime(timeLeft)}
            </div>
          </div>
          <div className="space-y-4">
            {/* QR Code */}
            <div className="flex justify-center mb-6">
              <div className="bg-white p-4 rounded-lg">
                <QRCodeSVG 
                  value={`litecoin:${orderDetails.address}?amount=${orderDetails.amountLTC}`}
                  size={200}
                  level="H"
                  includeMargin={true}
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Send exactly this amount:
              </label>
              <div className="glass-card p-3 rounded-lg flex items-center justify-between">
                <span className="text-white font-mono">{orderDetails.amountLTC} LTC</span>
                <button 
                  onClick={() => navigator.clipboard.writeText(orderDetails.amountLTC)}
                  className="text-[#22c55e] hover:text-[#15803d] transition-colors"
                >
                  Copy
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                To this address:
              </label>
              <div className="glass-card p-3 rounded-lg flex items-center justify-between">
                <span className="text-white font-mono break-all">{orderDetails.address}</span>
                <button 
                  onClick={() => navigator.clipboard.writeText(orderDetails.address)}
                  className="text-[#22c55e] hover:text-[#15803d] transition-colors ml-2"
                >
                  Copy
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (orderDetails.paymentMethod === 'paypal') {
      return (
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-white">PayPal Payment Details</h3>
            <div className="text-sm text-[#22c55e]">
              Expires in: {formatTime(timeLeft)}
            </div>
          </div>
          <div className="space-y-4">
            <div className="glass-card p-4 bg-[#22c55e]/10 border border-[#22c55e]/20">
              <div className="mb-4">
                <p className="text-[#22c55e] font-medium mb-2">Important Instructions:</p>
                <ul className="list-disc list-inside text-sm text-[#22c55e] space-y-1">
                  <li>Send payment as "Friends & Family" to avoid fees</li>
                  <li>Include your Order ID in the payment note</li>
                  <li>Do not mention any product or service in the note</li>
                </ul>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-[#22c55e] mb-2">
                  Send payment to:
                </label>
                <div className="bg-gray-800 p-3 rounded-lg flex items-center justify-between">
                  <code className="text-white font-mono text-sm">{process.env.REACT_APP_PAYPAL_EMAIL}</code>
                  <button 
                    onClick={() => navigator.clipboard.writeText(process.env.REACT_APP_PAYPAL_EMAIL)}
                    className="text-[#22c55e] hover:text-white transition-colors text-sm ml-4"
                  >
                    Copy
                  </button>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-[#22c55e] mb-2">
                  Amount to send:
                </label>
                <div className="bg-gray-800 p-3 rounded-lg flex items-center justify-between">
                  <span className="text-white font-mono">${orderDetails.amountUSD}</span>
                  <button 
                    onClick={() => navigator.clipboard.writeText(orderDetails.amountUSD)}
                    className="text-[#22c55e] hover:text-white transition-colors text-sm ml-4"
                  >
                    Copy
                  </button>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-[#22c55e] mb-2">
                  Order ID (include in payment note):
                </label>
                <div className="bg-gray-800 p-3 rounded-lg flex items-center justify-between">
                  <code className="text-white font-mono text-sm">{invoiceId}</code>
                  <button 
                    onClick={() => navigator.clipboard.writeText(invoiceId)}
                    className="text-[#22c55e] hover:text-white transition-colors text-sm ml-4"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  if (notFound) {
    return <NotFound />;
  }

  if (paymentStatus === 'out_of_stock') {
    return (
      <div>
        <div className="min-h-screen flex flex-col items-center justify-center text-center p-4">
          <h1 className="text-6xl font-bold mb-4 flex items-center gap-2">
            Out
            <div className="w-12 h-12 relative flex items-center justify-center animate-bounce">
              <div className="absolute inset-0 bg-[#22c55e] rounded-full opacity-10"></div>
              <div className="absolute inset-[3px] bg-[#22c55e] rounded-full opacity-20"></div>
              <div className="absolute inset-[6px] bg-[#22c55e] rounded-full opacity-40"></div>
              <div className="absolute inset-[9px] bg-[#22c55e] rounded-full"></div>
            </div>
            f Stock
          </h1>
          <p className="text-gray-400 mb-12 text-lg">
            This product is currently out of stock. Please contact us on Discord for assistance.
          </p>
          <div className="flex gap-6">
            <a
              href="https://discord.gg/olymp4"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#22c55e] hover:bg-[#1ea34b] text-white px-8 py-3 rounded-lg transition-all duration-200 transform hover:scale-105 font-medium text-lg shadow-lg shadow-[#22c55e]/20 flex items-center gap-3"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
              <span>JOIN DISCORD</span>
            </a>
            <Link
              to="/"
              className="bg-gray-800 hover:bg-gray-700 text-white px-8 py-3 rounded-lg transition-all duration-200 transform hover:scale-105 font-medium text-lg shadow-lg shadow-gray-800/20 flex items-center gap-3"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>RETURN TO STORE</span>
            </Link>
          </div>
        </div>
        <div className="fixed bottom-4 left-4 bg-[#141414] p-4 rounded-lg shadow-lg">
          <p className="text-gray-400 mb-2">Invoice ID:</p>
          <p className="font-mono text-[#22c55e]">{invoiceId}</p>
        </div>
      </div>
    );
  }

  if (!orderDetails) return null;

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  // eslint-disable-next-line no-unused-vars
  const getStatusDisplay = (order) => {
    // Si c'est une erreur de stock, priorité sur les autres statuts
    if (order.deliveryError === 'out_of_stock') {
      return {
        text: 'Out o',
        secondPart: 'f Stock',
        showAnimation: true
      };
    }
    
    if (order.status === 'delivery_failed') {
      return {
        text: 'Delivery Failed',
        showAnimation: false
      };
    }

    if (order.status === 'forced') {
      return {
        text: 'FORCED',
        showAnimation: false
      };
    }
    
    return {
      text: order.status.charAt(0).toUpperCase() + order.status.slice(1),
      showAnimation: false
    };
  };

  // eslint-disable-next-line no-unused-vars
  const getStatusColor = (status, deliveryError) => {
    if (deliveryError === 'out_of_stock') {
      return 'text-red-500';
    }
    if (status === 'delivery_failed') {
      return 'text-red-500';
    }
    if (status === 'out_of_stock') {
      return 'text-red-500';
    }
    if (status === 'forced') {
      return 'text-purple-500';
    }
    return 'text-white';
  };

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <Link 
          to="/" 
          onClick={handleBackToStore}
          className="inline-flex items-center space-x-2 text-gray-400 hover:text-white transition-colors duration-200 mb-4"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <span>Back to Store</span>
        </Link>
        <div className="glass-card overflow-hidden rounded-lg">
          {/* Header */}
          <div className="border-b border-white/10 p-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-white">Payment Invoice</h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center ${
                paymentStatus === 'forced' ? 'text-purple-500' : ''
              }`}>
                {(() => {
                  if (paymentStatus === 'out_of_stock' || orderDetails?.deliveryError === 'out_of_stock') {
                    return (
                      <div className="flex items-center">
                        <span>Out o</span>
                        <div className="w-2 h-2 mx-0.5 relative">
                          <div className="absolute inset-0 border border-[#22c55e] rounded-full animate-spin"></div>
                        </div>
                        <span>f Stock</span>
                      </div>
                    );
                  }
                  return paymentStatus.charAt(0).toUpperCase() + paymentStatus.slice(1);
                })()}
              </span>
            </div>
            <p className="mt-2 text-sm text-gray-400">Invoice ID: {invoiceId}</p>
          </div>

          {/* Order Details */}
          <div className="p-6 space-y-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-lg font-medium text-white">{orderDetails.product.title}</h2>
                <p className="text-sm text-gray-400">Quantity: {orderDetails.quantity}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-400">Amount Due</p>
                {orderDetails.paymentMethod === 'litecoin' ? (
                  <>
                    <p className="text-lg font-medium text-white">{orderDetails.amountLTC} LTC</p>
                    <p className="text-sm text-gray-400">≈ ${orderDetails.amountUSD}</p>
                  </>
                ) : (
                  <p className="text-lg font-medium text-white">${orderDetails.amountUSD}</p>
                )}
              </div>
            </div>

            {/* Payment Instructions */}
            {paymentStatus === 'pending' && renderPaymentInstructions()}

            {/* Status Messages and Delivered Items */}
            {paymentStatus === 'cancelled' && (
              <div className="bg-red-500/20 text-red-500 p-4 rounded-lg">
                This order has been cancelled. Please create a new order if you wish to proceed with the purchase.
              </div>
            )}
            {paymentStatus === 'paid' && (
              <div className="bg-green-500/20 text-green-500 p-4 rounded-lg">
                {orderDetails.product.deliveryType === 'serials' ? (
                  'Payment received! Your serials will be delivered instantly...'
                ) : (
                  'Payment received! Your product is being prepared for delivery...'
                )}
              </div>
            )}
            {paymentStatus === 'delivery_failed' && (
              <div className="bg-red-500/20 text-red-500 p-4 rounded-lg">
                Your order failed due to API, contact support
              </div>
            )}
            {(paymentStatus === 'delivered' || paymentStatus === 'forced') && orderDetails.deliveredItems && renderDeliveredSection()}

            {/* Boost Instructions */}
            {(paymentStatus === 'delivered' || paymentStatus === 'forced') && orderDetails.product.type === 'boost' && orderDetails.product.showBoostInstructions && (
              <div className="bg-[#1f1f1f] p-4 rounded-md text-sm text-gray-300 mt-4">
                <h3 className="font-medium text-[#22c55e] mb-2">How to use your server boost key:</h3>
                <ol className="list-decimal list-inside space-y-2">
                  <li>Go to <a href="https://boostbot.online/api/redeem-order/1335995797245792345" target="_blank" rel="noopener noreferrer" className="text-[#22c55e] hover:text-[#15803d]">https://boostbot.online/api/redeem-order/1335995797245792345</a></li>
                  <li>Paste your server boost key in the designated field</li>
                  <li>Enter your Discord server link</li>
                  <li>Click "Redeem" to activate your server boost</li>
                </ol>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="glass-card p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Return to Store?</h3>
            <p className="text-gray-300 mb-6">
              If you have already sent a payment and haven't received your items yet, the funds may be lost if you leave this page. Please wait for the payment confirmation.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 rounded-lg bg-gray-600 text-white hover:bg-gray-500 transition-colors"
              >
                Stay Here
              </button>
              <button
                onClick={handleConfirmReturn}
                className="px-4 py-2 rounded-lg bg-[#22c55e] text-white hover:bg-[#15803d] transition-colors"
              >
                Return to Store
              </button>
            </div>
          </div>
        </div>
      )}

      {showFeedbackForm && !existingFeedback && !feedbackSubmitted && (
        <FeedbackForm
          orderId={invoiceId}
          onClose={() => {
            setShowFeedbackForm(false);
            setFeedbackSubmitted(true);
          }}
          onFeedbackSubmitted={(feedback) => {
            setExistingFeedback(feedback);
            setFeedbackSubmitted(true);
            setShowFeedbackForm(false);
          }}
        />
      )}
    </div>
  );
};

export default PaymentPage; 